// GLOBAL-NAV-1 — the persistent in-app navigation bar (below the top nav, NOT
// browser chrome). History-driven Back / Forward + a clickable breadcrumb trail,
// plus Recent / Pinned menus and a pin-the-current-page star (Phase 3).
import { useState, useRef, useEffect } from "react";
import { useNavigation } from "./NavigationContext";
import { breadcrumbChain, navLabel, NAV_REGISTRY, locFromPref } from "./navRegistry";
import { reportViewRefId } from "../../reports/reportRegistry";
import { can } from "../../api";

const BTN = (enabled) => ({
  border: "1px solid var(--border)", background: "#fff", borderRadius: 6,
  width: 28, height: 26, fontSize: 14, lineHeight: "22px", textAlign: "center",
  color: enabled ? "var(--text2)" : "var(--text3)", cursor: enabled ? "pointer" : "default",
  opacity: enabled ? 1 : 0.45, padding: 0,
});
const PILL = { border: "1px solid var(--border)", background: "#fff", borderRadius: 6, padding: "3px 10px",
  fontSize: 12, color: "var(--text2)", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" };

function truncate(crumbs) {
  if (crumbs.length <= 4) return crumbs;
  return [crumbs[0], { ellipsis: true }, crumbs[crumbs.length - 2], crumbs[crumbs.length - 1]];
}
function Menu({ label, items, empty, onPick, onReorder }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  const arrow = { border: "1px solid var(--border)", background: "#fff", borderRadius: 4, width: 18, height: 18, fontSize: 10, lineHeight: "14px", cursor: "pointer", color: "var(--text2)", padding: 0 };
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button style={PILL} onClick={() => setOpen((o) => !o)}>{label} <span style={{ fontSize: 9 }}>▼</span></button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 4px)", right: 0, minWidth: 260, maxHeight: 360, overflow: "auto",
          background: "#fff", border: "1px solid var(--border)", borderRadius: 8, boxShadow: "0 6px 20px rgba(0,0,0,.12)", zIndex: 50, padding: 4 }}>
          {items.length === 0 ? (
            <div style={{ padding: "10px 12px", fontSize: 12, color: "var(--text3)" }}>{empty}</div>
          ) : items.map((r, i) => (
            <div key={`${r.kind}:${r.ref_id}`} style={{ display: "flex", alignItems: "center", gap: 4, borderRadius: 6 }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--bg2,#f1f5f9)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "none")}>
              <button onClick={() => { setOpen(false); onPick(r); }}
                style={{ flex: 1, textAlign: "left", padding: "7px 10px", fontSize: 13, background: "none", border: "none", cursor: "pointer", color: "var(--text)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                <span style={{ color: "var(--text3)", fontSize: 10, marginRight: 6, textTransform: "uppercase" }}>{r.kind === "page" ? "" : r.kind}</span>
                {r.label}
              </button>
              {onReorder && (
                <span style={{ display: "inline-flex", gap: 2, paddingRight: 6 }}>
                  <button style={{ ...arrow, opacity: i === 0 ? 0.35 : 1 }} disabled={i === 0} title="Move up" onClick={(e) => { e.stopPropagation(); onReorder(r.id, -1); }}>↑</button>
                  <button style={{ ...arrow, opacity: i === items.length - 1 ? 0.35 : 1 }} disabled={i === items.length - 1} title="Move down" onClick={(e) => { e.stopPropagation(); onReorder(r.id, 1); }}>↓</button>
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function GlobalNavBar() {
  const { current, canBack, canForward, back, forward, navigate, pins, recents, isPinned, togglePin, reorderPins, openBug, bugUnread } = useNavigation();
  const showBug = can("file_bug");
  const crumbs = truncate(breadcrumbChain(current));
  const movePin = (id, dir) => {
    const ids = pins.map((p) => p.id);
    const i = ids.indexOf(id), j = i + dir;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    reorderPins(ids);
  };

  const reg = NAV_REGISTRY[current.page] || {};
  const pinEligible = !!reg.pinEligible || current.entityId != null;
  let pinKind = current.entityId != null ? (reg.entityType || "entity") : "page";
  let pinRef = current.entityId != null ? String(current.entityId) : current.page;
  // REPORTS-UX-1: on the Reports page, pin the granular VIEW — "WIP — Team Rollup"
  // and "WIP — Claims I Lead" are distinct pins; a filter tweak upserts the same.
  if (current.page === "reports" && current.params?.report) {
    pinKind = "report_view";
    pinRef = reportViewRefId(current.params.report, current.params.view);
  }
  const pinned = isPinned(pinKind, pinRef);
  const pinCurrent = () => togglePin({
    kind: pinKind, ref_id: pinRef, page: current.page, label: navLabel(current), params: current.params || {},
  });

  // Open the current view again. New tab = same browser window (no window features). New window =
  // a separate OS window cascaded onto the SAME monitor (clamp to the current screen's bounds).
  // Both are same-origin so they stay logged in (token is in localStorage).
  const openNewTab = () => window.open(window.location.href, "_blank");
  const openNewWindow = () => {
    const scr = window.screen || {};
    const sLeft = scr.availLeft || 0, sTop = scr.availTop || 0;
    const sW = scr.availWidth || 1440, sH = scr.availHeight || 900;
    const w = Math.min(1320, sW - 80), h = Math.min(920, sH - 80);
    const baseLeft = (Number.isFinite(window.screenX) ? window.screenX : sLeft) + 60;
    const baseTop = (Number.isFinite(window.screenY) ? window.screenY : sTop) + 60;
    const left = Math.max(sLeft, Math.min(baseLeft, sLeft + sW - w));
    const top = Math.max(sTop, Math.min(baseTop, sTop + sH - h));
    window.open(window.location.href, `atticus_${Date.now()}`, `width=${w},height=${h},left=${left},top=${top}`);
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 16px",
      borderBottom: "1px solid var(--border)", background: "var(--bg2,#f8fafc)", minHeight: 38 }}>
      <div style={{ display: "flex", gap: 4 }}>
        <button style={BTN(canBack)} disabled={!canBack} onClick={back} title="Back">‹</button>
        <button style={BTN(canForward)} disabled={!canForward} onClick={forward} title="Forward">›</button>
        <button style={BTN(true)} onClick={openNewTab} title="Open this view in a new tab (same window)">⊞</button>
        <button style={BTN(true)} onClick={openNewWindow} title="Open this view in a new window (same screen)">⧉</button>
      </div>
      <nav style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 13, flexWrap: "wrap", flex: 1, minWidth: 0 }}>
        {crumbs.map((c, i) => (
          <span key={i} style={{ display: "flex", alignItems: "center", gap: 4 }}>
            {i > 0 && <span style={{ color: "var(--text3)" }}>›</span>}
            {c.ellipsis ? <span style={{ color: "var(--text3)" }}>…</span>
              : c.isCurrent ? <span style={{ fontWeight: 600, color: "var(--navy,#1F3864)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 360 }}>{c.label}</span>
              : <button onClick={() => navigate(c.loc)} title={`Go to ${c.label}`} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--text2)", fontSize: 13 }}>{c.label}</button>}
          </span>
        ))}
      </nav>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {showBug && (
          <button onClick={openBug} title="Report a bug or request a feature"
            style={{ ...PILL, position: "relative", padding: "3px 8px", fontSize: 15, lineHeight: 1 }}>
            <span role="img" aria-label="Report a bug">🐞</span>
            {bugUnread > 0 && (
              <span aria-label={`${bugUnread} open`} style={{ position: "absolute", top: -5, right: -5,
                minWidth: 15, height: 15, padding: "0 3px", borderRadius: 8, background: "#dc2626", color: "#fff",
                fontSize: 9, fontWeight: 700, lineHeight: "15px", textAlign: "center", boxSizing: "border-box" }}>
                {bugUnread > 9 ? "9+" : bugUnread}
              </span>
            )}
          </button>
        )}
        {pinEligible && (
          <button onClick={pinCurrent} title={pinned ? "Unpin this" : "Pin this"}
            style={{ ...PILL, color: pinned ? "#b45309" : "var(--text3)", borderColor: pinned ? "#fcd34d" : "var(--border)", background: pinned ? "#fffbeb" : "#fff" }}>
            {pinned ? "★" : "☆"} Pin
          </button>
        )}
        <Menu label="Recent" items={recents} empty="No recent items yet." onPick={(r) => navigate(locFromPref(r))} />
        <Menu label="Pinned" items={pins} empty="No pinned items. Use ☆ Pin." onPick={(r) => navigate(locFromPref(r))} onReorder={movePin} />
      </div>
    </div>
  );
}
